import { ProductMatrixSectionGeneral } from '@app/components/products/ProductMatrix/ProductMatrixSectionGeneral';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getActiveDeliveryWindowsIdsForOrderBuyer, getIsProductLoading } from '@app/ducks';
import { getHasOptimisticUpdatesByProductId, getOrderDetails, removeProductsFromOrder } from '@app/ducks/order';
import { getDeliveryWindowIdsFromVariants, getIsExpired } from '@app/logic/deliveryWindows';
import { getHasEtaInSelectedDeliveryWindows } from '@app/logic/eta';
import {
  getDeliveryWindowsFromProducts,
  getProductByProductIdVariantIdFromProducts,
  getTotalProductPriceInProductVariants,
  getTotalUnitsInProductVariants,
  getTotalVariantQuantityInProductVariants,
} from '@app/logic/Orders';
import ProductsLogic from '@app/logic/products';
import { useRemoveExpiredSelectionProducts } from '@app/services/hooks/selection/useRemoveExpiredSelectionProducts';
import { useRemoveSelectionProducts } from '@app/services/hooks/selection/useRemoveSelectionProducts';
import { GAProductListItemId, GAProductListItemName, mapProductToGAItem, useGTM } from '@app/utils/analytics';
import { cn } from '@app/utils/cn';
import { useIsScreenSize } from '@app/utils/hooks';
import { useConfirmationGuard } from '@app/utils/hooks/useConfirmationGuard';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Product } from '@typings';
import { prop, uniqBy } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useOrderProductsContext } from '../OrderProducts/OrderProductsContext';

import { OrderProductAlerts } from './OrderProductAlerts';
import { OrderProductInfo } from './OrderProductInfo';
import { OrderProductInfoMobile } from './OrderProductInfoMobile';

interface Props {
  deliveryWindowIds?: string[];
  productInfo: Product.Standard;
  stockTypeLabel?: string;
}
export interface SummaryValues {
  variantsCount: number;
  unitsCount: number;
  price: number;
}

export const OrderProduct = (props: Props) => {
  const { deliveryWindowIds, productInfo, stockTypeLabel } = props;
  const selectedDeliveryWindowIds = React.useMemo(() => deliveryWindowIds ?? [], [deliveryWindowIds]);
  const { t } = useTranslation(['expiredDelwins', 'confirmationConfig', 'orders', 'products', 'selections', 'cancelled']);
  const productId = productInfo.product;
  const variantId = productInfo.variant;
  const orderDetails = useSelector(getOrderDetails);
  const activeDeliveryWindowsIds = useSelector(getActiveDeliveryWindowsIdsForOrderBuyer);
  const dispatch = useDispatch();
  const { trackGTMEvent } = useGTM();
  const confirmationGuard = useConfirmationGuard();
  const { sortByByDeliveryWindow, sortByStockType, orderDeliveryWindows } = useOrderProductsContext();
  const isProductLoadingSelector = React.useMemo(() => getIsProductLoading(productId), [productId]);
  const isLoadingProduct = useSelector(isProductLoadingSelector);
  const isMutatingUnitsSelector = React.useMemo(() => getHasOptimisticUpdatesByProductId(productId), [productId]);
  const isMutatingUnits = useSelector(isMutatingUnitsSelector);
  const isRemoving = !isMutatingUnits && isLoadingProduct;
  const showMobileVersion = useIsScreenSize(BREAKPOINTS.MD);
  const { currency } = orderDetails.currency;

  const removeConfirmationContent: { title: string; content: string } = React.useMemo(() => {
    if (sortByByDeliveryWindow) {
      return {
        content: t('confirmationConfig:remove_product_from_order_by_delwin.content'),
        title: t('confirmationConfig:remove_product_from_order_by_delwin.title'),
      };
    }

    if (sortByStockType) {
      return {
        content: t('confirmationConfig:remove_product_from_order_by_stock_type.content', { stockName: stockTypeLabel }),
        title: t('confirmationConfig:remove_product_from_order_by_stock_type.title', { stockName: stockTypeLabel }),
      };
    }

    return {
      content: t('confirmationConfig:remove_product_from_order.content'),
      title: t('confirmationConfig:remove_product_from_order.title'),
    };
  }, [sortByByDeliveryWindow, sortByStockType, stockTypeLabel, t]);

  const removeProduct = confirmationGuard(() => {
    return {
      content: removeConfirmationContent.content,
      onOk: () => {
        trackGTMEvent('remove_from_cart', {
          ecommerce: {
            currency,
            items: [
              mapProductToGAItem(productInfo, {
                item_list_id: GAProductListItemId.Selection,
                item_list_name: GAProductListItemName.Selection,
                quantity: summaryValues.unitsCount,
              }),
            ],
            value: summaryValues.price,
          },
        });
        dispatch(
          removeProductsFromOrder({
            deliveryWindows: selectedDeliveryWindowIds,
            products: [productId],
          }),
        );
      },
      title: removeConfirmationContent.title,
    };
  });
  const productVariantsInfo = ProductsLogic.getAllOrderedVariants(productInfo);
  const productVariantDeliveryWindowIds = getDeliveryWindowIdsFromVariants(productVariantsInfo);
  const orderProductInfo = React.useMemo(
    () => getProductByProductIdVariantIdFromProducts(productId, variantId, orderDetails.order.products),
    [orderDetails.order.products, productId, variantId],
  );

  const allOrderProductVariants = React.useMemo(
    () => orderDetails.order.products.filter(product => product.product === productId),
    [orderDetails.order.products, productId],
  );

  const orderVariantsFilteredBySelectedDeliveryWindows = React.useMemo(() => {
    return !isEmpty(selectedDeliveryWindowIds) ?
        allOrderProductVariants.filter(product => selectedDeliveryWindowIds.includes(product.deliveryWindow))
      : allOrderProductVariants;
  }, [selectedDeliveryWindowIds, allOrderProductVariants]);

  const hasEtaBadge = React.useMemo(
    () =>
      getHasEtaInSelectedDeliveryWindows(
        orderVariantsFilteredBySelectedDeliveryWindows.map(deliveryWindow => deliveryWindow.deliveryWindow),
        productVariantsInfo,
      ),
    [orderVariantsFilteredBySelectedDeliveryWindows, orderDeliveryWindows],
  );

  const cancelledSelectedOrderVariants = React.useMemo(() => {
    return orderVariantsFilteredBySelectedDeliveryWindows.filter(product => product.isCancelled);
  }, [orderVariantsFilteredBySelectedDeliveryWindows]);

  const uniqueCancelledSelectedOrderVariants = React.useMemo(
    () => uniqBy(prop('variant'), cancelledSelectedOrderVariants),
    [cancelledSelectedOrderVariants],
  );

  const isOrderProductCancelled = cancelledSelectedOrderVariants.length === orderVariantsFilteredBySelectedDeliveryWindows.length;
  const summaryValues: SummaryValues = React.useMemo(
    () => ({
      price: getTotalProductPriceInProductVariants(orderVariantsFilteredBySelectedDeliveryWindows),
      unitsCount: getTotalUnitsInProductVariants(orderVariantsFilteredBySelectedDeliveryWindows),
      variantsCount: getTotalVariantQuantityInProductVariants(orderVariantsFilteredBySelectedDeliveryWindows),
    }),
    [orderVariantsFilteredBySelectedDeliveryWindows],
  );

  const orderProductDeliveryWindows = getDeliveryWindowsFromProducts(
    orderVariantsFilteredBySelectedDeliveryWindows,
    orderDetails.deliveryWindows,
  );
  const expiredOrderProductDeliveryWindows = React.useMemo(
    () => orderProductDeliveryWindows.filter(deliveryWindow => getIsExpired(deliveryWindow, activeDeliveryWindowsIds)),
    [activeDeliveryWindowsIds, orderProductDeliveryWindows],
  );

  const expiredOrderVariants = React.useMemo(() => {
    const expiredOrderDelwinIds = expiredOrderProductDeliveryWindows.map(deliveryWindow => deliveryWindow.deliveryWindow);

    return orderVariantsFilteredBySelectedDeliveryWindows.filter(({ deliveryWindow }) => expiredOrderDelwinIds.includes(deliveryWindow));
  }, [expiredOrderProductDeliveryWindows, orderVariantsFilteredBySelectedDeliveryWindows]);
  const uniqueExpiredOrderVariants = React.useMemo(() => uniqBy(prop('variant'), expiredOrderVariants), [expiredOrderVariants]);

  const orderProductDeliveryWindowIds = React.useMemo(() => {
    if (isEmpty(selectedDeliveryWindowIds)) {
      return productVariantDeliveryWindowIds;
    }

    return productVariantDeliveryWindowIds.filter(deliveryWindowId => selectedDeliveryWindowIds.includes(deliveryWindowId));
  }, [selectedDeliveryWindowIds, productVariantDeliveryWindowIds]);

  const isAllOrderVariantsExpired = expiredOrderVariants.length === orderVariantsFilteredBySelectedDeliveryWindows.length;
  const hasNoActiveDeliveryWindows = isEmpty(orderProductDeliveryWindowIds);
  const removeCancelledVariants = useRemoveSelectionProducts(cancelledSelectedOrderVariants);
  const removeExpiredVariants = useRemoveExpiredSelectionProducts({
    expiredDeliveryWindows: expiredOrderProductDeliveryWindows,
    orderProducts: uniqueExpiredOrderVariants,
  });

  /**
   * This is probably deleted product - should be present in `order.deletedProducts`.
   */
  if (!isDefined(orderProductInfo)) {
    return null;
  }

  const isDisabled = !orderProductInfo.isEditable || hasNoActiveDeliveryWindows || isOrderProductCancelled || isAllOrderVariantsExpired;

  return (
    <div className="mx-auto rounded-regular bg-white p-4 lg:px-8 lg:py-5">
      <OrderProductAlerts
        orderProductInfo={orderProductInfo}
        cancelledVariants={uniqueCancelledSelectedOrderVariants}
        expiredDeliveryWindows={expiredOrderProductDeliveryWindows}
        expiredVariants={uniqueExpiredOrderVariants}
        removeCancelledVariants={removeCancelledVariants}
        removeExpiredVariants={removeExpiredVariants}
      />
      {showMobileVersion ?
        <OrderProductInfoMobile
          productInfo={productInfo}
          onRemove={removeProduct}
          summaryValues={summaryValues}
          isDisabled={isDisabled}
          hasEtaBadge={hasEtaBadge}
          isRemoving={isRemoving}
          isMutatingUnits={isMutatingUnits}
        />
      : <OrderProductInfo
          productInfo={productInfo}
          onRemove={removeProduct}
          summaryValues={summaryValues}
          isDisabled={isDisabled}
          isRemoving={isRemoving}
          hasEtaBadge={hasEtaBadge}
          isMutatingUnits={isMutatingUnits}
      />
      }
      <div className={cn('overflow-hidden', { 'pointer-events-none': isDisabled || isRemoving })}>
        <ProductMatrixSectionGeneral
          showOnlyDeliveryWindows={deliveryWindowIds}
          variants={productVariantsInfo}
          attributesSource="listing"
        />
      </div>
    </div>
  );
};
